import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch, parseJsonSafe, handleUnauthorized } from '../../utils/api';

// Plantillas rápidas y destinos (deep link dentro de la app móvil)
const TEMPLATES = [
  { label: 'Respuesta de soporte', title: 'Tienes respuesta de soporte', body: 'Tu ticket tiene una nueva respuesta. Entra para verla.', route: '/soporte' },
  { label: 'Factura lista', title: 'Tu factura está lista', body: 'Ya puedes consultar y descargar tu factura desde la app.', route: '/facturas' },
  { label: 'Presupuesto enviado', title: 'Tienes un presupuesto nuevo', body: 'Te hemos enviado un presupuesto. Puedes revisarlo en la app.', route: '/presupuestos' },
  { label: 'Proyecto actualizado', title: 'Actualización de tu proyecto', body: 'Hemos actualizado el estado de tu proyecto.', route: '/proyectos' },
];
const ROUTES = [
  { value: '', label: 'No abrir nada en concreto' },
  { value: '/', label: 'Inicio' },
  { value: '/facturas', label: 'Facturas' },
  { value: '/presupuestos', label: 'Presupuestos' },
  { value: '/proyectos', label: 'Proyectos' },
  { value: '/horas', label: 'Bolsas de horas' },
  { value: '/servicios', label: 'Servicios' },
  { value: '/licencias', label: 'Licencias' },
  { value: '/infraestructura', label: 'Dominios y hosting' },
  { value: '/soporte', label: 'Soporte' },
  { value: '/perfil', label: 'Mi perfil' },
];

const PushNotificationsPage = () => {
  const token = localStorage.getItem('token');
  const [recipients, setRecipients] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('all'); // 'all' | 'select'
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({ title: '', body: '', route: '' });
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('');

  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = async () => {
    setLoading(true);
    try {
      const [rRes, hRes] = await Promise.all([
        apiFetch('/api/clients/push/recipients', { headers: auth }),
        apiFetch('/api/clients/push/history?limit=50', { headers: auth }),
      ]);
      if (rRes.status === 401 || hRes.status === 401) return handleUnauthorized();
      const r = await parseJsonSafe(rRes);
      const h = await parseJsonSafe(hRes);
      if (Array.isArray(r)) setRecipients(r);
      if (Array.isArray(h)) setHistory(h);
    } catch (_e) { /* noop */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const totalDevices = recipients.reduce((s, r) => s + (r.devices || 0), 0);
  const visible = recipients.filter((r) => (r.razon_social || '').toLowerCase().includes(filter.toLowerCase()));

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const send = async () => {
    const title = form.title.trim();
    const body = form.body.trim();
    if (!title || !body) return alert('Escribe un título y un mensaje.');
    const payload = mode === 'all' ? { all: true } : { clientIds: selected };
    if (mode === 'select' && !selected.length) return alert('Selecciona al menos un cliente.');
    const destinatarios = mode === 'all' ? recipients.length : selected.length;
    if (!window.confirm(`¿Enviar la notificación a ${destinatarios} cliente(s)?`)) return;

    setBusy(true);
    try {
      const res = await apiFetch('/api/clients/push/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({ ...payload, title, body, route: form.route || undefined }),
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((data && data.message) || 'No se pudo enviar');
      alert(data.message || 'Enviada');
      setForm({ title: '', body: '', route: '' });
      setSelected([]);
      load();
    } catch (err) {
      alert(err.message);
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="mb-1">Notificaciones push</h2>
          <div className="text-muted small">
            Envío manual a la app móvil de tus clientes. {recipients.length} cliente(s) con la app · {totalDevices} dispositivo(s).
          </div>
        </div>
        <Link className="btn btn-outline-secondary" to="/clientes">Volver a clientes</Link>
      </div>

      <div className="row g-3">
        {/* Formulario */}
        <div className="col-lg-6">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-3">Nueva notificación</h5>

              <div className="mb-3">
                <label className="form-label small text-muted">Plantillas rápidas</label>
                <div className="d-flex flex-wrap gap-2">
                  {TEMPLATES.map((t) => (
                    <button key={t.label} type="button" className="btn btn-sm btn-outline-secondary"
                      onClick={() => setForm({ title: t.title, body: t.body, route: t.route })}>{t.label}</button>
                  ))}
                </div>
              </div>

              <div className="mb-2">
                <label className="form-label small">Título</label>
                <input className="form-control" maxLength={80} value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Ej: Tienes respuesta de soporte" />
              </div>
              <div className="mb-2">
                <label className="form-label small">Mensaje</label>
                <textarea className="form-control" rows={3} maxLength={300} value={form.body}
                  onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                  placeholder="Escribe el mensaje de la notificación" />
              </div>
              <div className="mb-3">
                <label className="form-label small">Al tocar la notificación, abrir</label>
                <select className="form-select" value={form.route}
                  onChange={(e) => setForm((f) => ({ ...f, route: e.target.value }))}>
                  {ROUTES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>

              <div className="mb-2">
                <label className="form-label small">Destinatarios</label>
                <div className="form-check">
                  <input className="form-check-input" type="radio" id="dest-all" checked={mode === 'all'} onChange={() => setMode('all')} />
                  <label className="form-check-label" htmlFor="dest-all">
                    Todos los que tienen la app <span className="text-muted">({recipients.length})</span>
                  </label>
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="radio" id="dest-sel" checked={mode === 'select'} onChange={() => setMode('select')} />
                  <label className="form-check-label" htmlFor="dest-sel">
                    Seleccionar clientes <span className="text-muted">({selected.length} elegidos)</span>
                  </label>
                </div>
              </div>

              {mode === 'select' && (
                <div className="mb-3">
                  <input className="form-control form-control-sm mb-2" placeholder="Buscar cliente..."
                    value={filter} onChange={(e) => setFilter(e.target.value)} />
                  <div className="border rounded" style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {visible.length === 0 && <div className="p-3 text-muted small">Ningún cliente con la app instalada.</div>}
                    {visible.map((r) => (
                      <label key={r.client_id} className="d-flex align-items-center gap-2 px-2 py-1 border-bottom" style={{ cursor: 'pointer' }}>
                        <input type="checkbox" className="form-check-input m-0"
                          checked={selected.includes(r.client_id)} onChange={() => toggle(r.client_id)} />
                        <span className="flex-grow-1 text-truncate">{r.razon_social || `Cliente ${r.client_id}`}</span>
                        <span className="badge bg-light text-dark">{r.devices} disp.</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="border rounded bg-light p-3 mb-3" aria-label="Vista previa de notificación">
                <div className="small text-muted mb-1">Vista previa</div>
                <strong style={{ overflowWrap: 'anywhere' }}>{form.title || 'Título de la notificación'}</strong>
                <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{form.body || 'Mensaje de la notificación'}</div>
                <div className="small text-muted mt-2" role="status">Destinatarios: {mode === 'all' ? recipients.length : selected.length} clientes · {recipients.filter((r) => mode === 'all' || selected.includes(r.client_id)).reduce((sum, r) => sum + Number(r.devices || 0), 0)} dispositivos</div>
              </div>
              <button className="btn btn-primary w-100" onClick={send}
                disabled={busy || !form.title.trim() || !form.body.trim() || (mode === 'select' && !selected.length)}>
                {busy ? 'Enviando...' : 'Enviar notificación'}
              </button>
            </div>
          </div>
        </div>

        {/* Historial */}
        <div className="col-lg-6">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-3">Historial de envíos</h5>
              {loading && <div className="text-muted small">Cargando...</div>}
              {!loading && history.length === 0 && <div className="text-muted small">Todavía no se ha enviado ninguna notificación.</div>}
              {history.length > 0 && (
                <div className="table-responsive" style={{ maxHeight: 520, overflowY: 'auto' }}>
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Fecha</th><th>Cliente</th><th>Notificación</th><th className="text-end">Llegó a</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h) => (
                        <tr key={h.id}>
                          <td className="text-muted small text-nowrap">
                            {new Date(h.created_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="small">
                            {h.client_id
                              ? <Link to={`/clientes/${h.client_id}`}>{h.razon_social || `Cliente ${h.client_id}`}</Link>
                              : <span className="text-muted">—</span>}
                          </td>
                          <td className="small">
                            <div className="fw-semibold text-truncate" style={{ maxWidth: 220 }}>{h.title}</div>
                            <div className="text-muted text-truncate" style={{ maxWidth: 220 }}>{h.body}</div>
                            {h.route && <span className="badge bg-light text-dark">abre {h.route}</span>}
                          </td>
                          <td className="text-end small text-nowrap">
                            <span className={h.sent > 0 ? 'text-success' : 'text-muted'}>{h.sent}</span>
                            <span className="text-muted">/{h.devices}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationsPage;
